import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  UnifiedProgram, 
  Room, 
  BusRoute, 
  UserPreferences, 
  FilterOptions,
  Department,
  FavoriteItem 
} from '../types';
import { DataProcessor } from '../utils/dataProcessor';
import { findUserProgram } from '../utils/programMatching';

interface AppStore {
  // Data
  programs: UnifiedProgram[];
  rooms: Room[];
  buses: BusRoute[];
  departments: Department[];
  
  // User state
  userPreferences: UserPreferences | null;
  favorites: FavoriteItem[];
  searchHistory: string[];
  
  // UI state
  isLoading: boolean;
  currentView: 'welcome' | 'personal' | 'browse' | 'transport' | 'rooms' | 'calendar' | 'feedback';
  filters: FilterOptions;
  searchQuery: string;
  
  // Actions
  initializeData: () => Promise<void>;
  setUserPreferences: (preferences: UserPreferences) => void;
  clearUserPreferences: () => void;
  addToFavorites: (item: FavoriteItem) => void;
  removeFromFavorites: (id: string) => void;
  clearFavorites: () => void;
  addToSearchHistory: (query: string) => void;
  clearSearchHistory: () => void;
  setCurrentView: (view: AppStore['currentView']) => void;
  setFilters: (filters: FilterOptions) => void;
  setSearchQuery: (query: string) => void;
  
  // Computed getters
  getFilteredPrograms: () => UnifiedProgram[];
  getUserPrograms: () => UnifiedProgram[];
  getFavoritePrograms: () => UnifiedProgram[];
  searchPrograms: (query: string) => UnifiedProgram[];
  searchRooms: (query: string) => Room[];
}

export const useStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      programs: [],
      rooms: [],
      buses: [],
      departments: [],
      
      userPreferences: (() => {
        // Try to load from backup localStorage on init
        try {
          const saved = localStorage.getItem('userPreferences');
          if (saved) {
            console.log('🔄 Restored user preferences from localStorage backup');
            return JSON.parse(saved);
          }
        } catch (error) {
          console.error('Failed to restore from localStorage:', error);
        }
        return null;
      })(),
      favorites: [],
      searchHistory: [],
      
      isLoading: true,
      currentView: 'welcome',
      filters: {},
      searchQuery: '',
      
      // Actions
      initializeData: async () => {
        set({ isLoading: true });
        
        try {
          const dataProcessor = DataProcessor.getInstance();
          await dataProcessor.initializeData();
          
          const programs = dataProcessor.getAllPrograms();
          const rooms = dataProcessor.getAllRooms();
          const buses = dataProcessor.getAllBuses();
          const departments = dataProcessor.getAllDepartments();
          
          
          set({
            programs,
            rooms,
            buses,
            departments,
            isLoading: false
          });
        } catch (error) {
          set({ isLoading: false });
        }
      },
      
      setUserPreferences: (preferences) => {
        console.log('💾 Setting user preferences:', preferences);
        set({ userPreferences: preferences });
        
        // Also save to localStorage as backup
        try {
          localStorage.setItem('userPreferences', JSON.stringify(preferences));
          console.log('✅ User preferences saved to localStorage');
        } catch (error) {
          console.error('❌ Failed to save to localStorage:', error);
        }
      },
      
      clearUserPreferences: () => {
        set({ userPreferences: null, currentView: 'welcome' });
        try {
          localStorage.removeItem('userPreferences');
        } catch {
          // The persisted store is still cleared when browser storage is unavailable.
        }
      },
      
      addToFavorites: (item) => {
        const { favorites } = get();
        if (!favorites.find(fav => fav.id === item.id)) {
          set({ favorites: [...favorites, item] });
        }
      },
      
      removeFromFavorites: (id) => {
        const { favorites } = get();
        set({ favorites: favorites.filter(fav => fav.id !== id) });
      },
      
      clearFavorites: () => {
        set({ favorites: [] });
      },
      
      addToSearchHistory: (query) => {
        const { searchHistory } = get();
        const trimmedQuery = query.trim().toLowerCase();
        
        if (trimmedQuery && !searchHistory.includes(trimmedQuery)) {
          const newHistory = [trimmedQuery, ...searchHistory.slice(0, 9)]; // Keep only 10 items
          set({ searchHistory: newHistory });
        }
      },
      
      clearSearchHistory: () => {
        set({ searchHistory: [] });
      },
      
      setCurrentView: (view) => {
        set({ currentView: view });
      },
      
      setFilters: (filters) => {
        set({ filters });
      },
      
      setSearchQuery: (query) => {
        set({ searchQuery: query });
      },
      
      // Computed getters
      getFilteredPrograms: () => {
        const { programs, filters } = get();
        
        const results = programs.filter(program => {
          if (filters.department && program.department.toLowerCase() !== filters.department.toLowerCase()) {
            return false;
          }
          if (filters.category) {
            const programName = program.program.toLowerCase();
            const category = filters.category.toLowerCase();
            
            let matches = false;
            if (category === 'bachelor') {
              matches = programName.includes('bachelor') || 
                       programName.startsWith('bs ') || 
                       programName.startsWith('b.s ') ||
                       programName.startsWith('b.s.') ||
                       programName.includes(' bs ') ||
                       programName.includes('(bs)') ||
                       (programName.startsWith('b ') && !programName.includes('master'));
            } else if (category === 'master') {
              matches = programName.includes('master') || 
                       programName.startsWith('ms ') || 
                       programName.startsWith('m.s ') ||
                       programName.startsWith('m.s.') ||
                       programName.includes(' ms ') ||
                       programName.includes('(ms)') ||
                       programName.startsWith('mba') ||
                       programName.includes('m.b.a');
            }
            
            if (!matches) {
              return false;
            }
          }
          if (filters.program && !program.program.toLowerCase().includes(filters.program.toLowerCase())) {
            return false;
          }
          if (filters.semester && program.semester !== filters.semester) {
            return false;
          }
          if (filters.group && program.group !== filters.group) {
            return false;
          }
          if (filters.year && !program.years.includes(filters.year)) {
            return false;
          }
          // Combined teacher and day filter logic
          if (filters.teacher || filters.day) {
            let matchesTeacherAndDay = false;
            
            if (filters.teacher && filters.day) {
              // Both teacher and day filters are active - need to find the teacher teaching on that specific day
              const daySchedule = program.schedule[filters.day];
              if (daySchedule && Array.isArray(daySchedule) && daySchedule.length > 0) {
                matchesTeacherAndDay = daySchedule.some(course => {
                  const instructor = course.instructor || course.teacher;
                  return instructor && instructor.toLowerCase().trim() === filters.teacher!.toLowerCase().trim();
                });
              }
            } else if (filters.teacher && !filters.day) {
              // Only teacher filter - check if teacher exists in any day
              matchesTeacherAndDay = Object.values(program.schedule).some(courses =>
                courses.some(course => {
                  const instructor = course.instructor || course.teacher;
                  return instructor && instructor.toLowerCase().trim() === filters.teacher!.toLowerCase().trim();
                })
              );
            } else if (!filters.teacher && filters.day) {
              // Only day filter - check if there are classes on that day
              const daySchedule = program.schedule[filters.day];
              matchesTeacherAndDay = daySchedule && Array.isArray(daySchedule) && daySchedule.length > 0;
            }
            
            if (!matchesTeacherAndDay) {
              return false;
            }
          }
          
          // Course filter logic
          if (filters.course) {
            const courseMatches = Object.values(program.schedule).some(courses =>
              courses.some(course => {
                const courseName = course.course_name || course.course || '';
                return courseName.toLowerCase().includes(filters.course!.toLowerCase());
              })
            );
            
            if (!courseMatches) {
              return false;
            }
          }
          
          // Room filter logic
          if (filters.room) {
            const roomMatches = Object.values(program.schedule).some(courses =>
              courses.some(course => {
                const location = course.location || '';
                return location.toLowerCase().includes(filters.room!.toLowerCase());
              })
            );
            
            if (!roomMatches) {
              return false;
            }
          }
          
          return true;
        });
        
        return results;
      },
      
      getUserPrograms: () => {
        const { programs, userPreferences } = get();
        const program = findUserProgram(programs, userPreferences);
        return program ? [program] : [];
      },
      
      getFavoritePrograms: () => {
        const { programs, favorites } = get();
        const favoriteIds = favorites.filter(fav => fav.type === 'program').map(fav => fav.id);
        return programs.filter(program => favoriteIds.includes(program.id));
      },
      
      searchPrograms: (query) => {
        const dataProcessor = DataProcessor.getInstance();
        return dataProcessor.searchPrograms(query);
      },
      
      searchRooms: (query) => {
        const dataProcessor = DataProcessor.getInstance();
        return dataProcessor.searchRooms(query);
      }
    }),
    {
      name: 'university-timetable-storage',
      partialize: (state) => ({
        userPreferences: state.userPreferences,
        favorites: state.favorites,
        searchHistory: state.searchHistory,
        filters: state.filters
      })
    }
  )
);

// Selector hooks for better performance
export const usePrograms = () => useStore(state => state.programs);
export const useRooms = () => useStore(state => state.rooms);
export const useBuses = () => useStore(state => state.buses);
export const useDepartments = () => useStore(state => state.departments);
export const useUserPreferences = () => useStore(state => state.userPreferences);
export const useFavorites = () => useStore(state => state.favorites);
export const useCurrentView = () => useStore(state => state.currentView);
export const useFilters = () => useStore(state => state.filters);
export const useSearchQuery = () => useStore(state => state.searchQuery);
export const useIsLoading = () => useStore(state => state.isLoading);

// State selector hook
export const useStoreState = () => useStore(state => ({
  programs: state.programs,
  rooms: state.rooms,
  buses: state.buses,
  departments: state.departments,
  userPreferences: state.userPreferences,
  favorites: state.favorites,
  searchHistory: state.searchHistory,
  currentView: state.currentView,
  filters: state.filters,
  searchQuery: state.searchQuery,
  isLoading: state.isLoading
}));

// Action hooks
export const useStoreActions = () => useStore(state => ({
  initializeData: state.initializeData,
  setUserPreferences: state.setUserPreferences,
  clearUserPreferences: state.clearUserPreferences,
  addToFavorites: state.addToFavorites,
  removeFromFavorites: state.removeFromFavorites,
  clearFavorites: state.clearFavorites,
  addToSearchHistory: state.addToSearchHistory,
  clearSearchHistory: state.clearSearchHistory,
  setCurrentView: state.setCurrentView,
  setFilters: state.setFilters,
  setSearchQuery: state.setSearchQuery
}));

// Theme/Preferences Store
interface PreferencesState {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set) => ({
      theme: 'light',
      toggleTheme: () => set((state) => ({ theme: state.theme === 'light' ? 'dark' : 'light' })),
      setTheme: (theme) => set({ theme }),
    }),
    {
      name: 'preferences-storage',
    }
  )
);

// Settings Store
interface NotificationSettings {
  classReminders: boolean;
  reminderTime: number; // minutes before class
  scheduleChanges: boolean;
  weeklyDigest: boolean;
  pushNotifications: boolean;
  emailNotifications: boolean;
  soundEnabled: boolean;
}

interface AppPreferences {
  timeFormat: '12h' | '24h';
  weekStartsOn: 'Sunday' | 'Monday';
  defaultView: 'week' | 'day' | 'month';
  autoSync: boolean;
  compactMode: boolean;
  showWeekends: boolean;
  showFullTimetableOnDashboard: boolean;
  language: 'en' | 'ur';
}

interface PrivacySettings {
  shareUsageData: boolean;
  saveSearchHistory: boolean;
  rememberFilters: boolean;
}

interface SettingsState {
  notifications: NotificationSettings;
  preferences: AppPreferences;
  privacy: PrivacySettings;
  
  // Actions
  updateNotifications: (settings: Partial<NotificationSettings>) => void;
  updatePreferences: (settings: Partial<AppPreferences>) => void;
  updatePrivacy: (settings: Partial<PrivacySettings>) => void;
  resetToDefaults: () => void;
  exportSettings: () => string;
  importSettings: (settings: string) => boolean;
}

const defaultNotifications: NotificationSettings = {
  classReminders: true,
  reminderTime: 15,
  scheduleChanges: true,
  weeklyDigest: false,
  pushNotifications: true,
  emailNotifications: false,
  soundEnabled: true
};

const defaultPreferences: AppPreferences = {
  timeFormat: '12h',
  weekStartsOn: 'Monday',
  defaultView: 'week',
  autoSync: true,
  compactMode: false,
  showWeekends: true,
  showFullTimetableOnDashboard: false,
  language: 'en'
};

const defaultPrivacy: PrivacySettings = {
  shareUsageData: false,
  saveSearchHistory: true,
  rememberFilters: true
};

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      notifications: defaultNotifications,
      preferences: defaultPreferences,
      privacy: defaultPrivacy,
      
      updateNotifications: (newSettings) => {
        set(state => ({
          notifications: { ...state.notifications, ...newSettings }
        }));
      },
      
      updatePreferences: (newSettings) => {
        set(state => ({
          preferences: { ...state.preferences, ...newSettings }
        }));
      },
      
      updatePrivacy: (newSettings) => {
        set(state => ({
          privacy: { ...state.privacy, ...newSettings }
        }));
      },
      
      resetToDefaults: () => {
        set({
          notifications: defaultNotifications,
          preferences: defaultPreferences,
          privacy: defaultPrivacy
        });
      },
      
      exportSettings: () => {
        const state = get();
        return JSON.stringify({
          notifications: state.notifications,
          preferences: state.preferences,
          privacy: state.privacy,
          exportDate: new Date().toISOString(),
          version: '1.0.0'
        }, null, 2);
      },
      
      importSettings: (settingsJson) => {
        try {
          const imported = JSON.parse(settingsJson);
          if (imported.notifications && imported.preferences && imported.privacy) {
            set({
              notifications: { ...defaultNotifications, ...imported.notifications },
              preferences: { ...defaultPreferences, ...imported.preferences },
              privacy: { ...defaultPrivacy, ...imported.privacy }
            });
            return true;
          }
        } catch (error) {
        }
        return false;
      }
    }),
    {
      name: 'app-settings-storage',
    }
  )
);
